import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';
import toast from 'react-hot-toast';

const { FiPlus, FiTrash2, FiSave, FiX, FiEdit2 } = FiIcons;

const CustomFieldsTab = ({ user }) => {
    const [customFields, setCustomFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAddingField, setIsAddingField] = useState(false);
    const [editingFieldId, setEditingFieldId] = useState(null);

    // New field form state
    const [newField, setNewField] = useState({
        name: '',
        key: '',
        type: 'text',
        isRequired: false,
        options: [],
        defaultValue: '',
    });

    // Field types
    const fieldTypes = [
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
        { value: 'boolean', label: 'Boolean (Yes/No)' },
        { value: 'select', label: 'Select (Dropdown)' },
    ];

    useEffect(() => {
        loadCustomFields();
    }, []);

    const loadCustomFields = async () => {
        setLoading(true);
        try {
            const response = await api.get('/custom-fields');
            if (response.success) {
                setCustomFields(response.data);
            }
        } catch (error) {
            console.error('Error loading custom fields:', error);
            toast.error('Failed to load custom fields');
        } finally {
            setLoading(false);
        }
    };

    const handleAddField = async () => {
        if (!newField.name || !newField.key) {
            toast.error('Name and Key are required');
            return;
        }

        // Validate key format (lowercase, numbers, underscores)
        if (!/^[a-z0-9_]+$/.test(newField.key)) {
            toast.error('Key must contain only lowercase letters, numbers, and underscores');
            return;
        }

        // Validate select type has options
        if (newField.type === 'select' && newField.options.length === 0) {
            toast.error('Select fields must have at least one option');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: newField.name,
                key: newField.key,
                type: newField.type,
                isRequired: newField.isRequired,
                ...(newField.type === 'select' && { options: newField.options }),
                ...(newField.defaultValue && { defaultValue: newField.defaultValue }),
            };

            const response = await api.post('/custom-fields', payload);

            if (response.success) {
                toast.success('Custom field created successfully');
                setCustomFields([...customFields, response.data]);
                resetForm();
                setIsAddingField(false);
            }
        } catch (error) {
            console.error('Error creating custom field:', error);
            toast.error(error.message || 'Failed to create custom field');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateField = async (fieldId) => {
        const field = customFields.find(f => f.id === fieldId);
        if (!field) return;

        setLoading(true);
        try {
            const response = await api.patch(`/custom-fields/${fieldId}`, {
                name: field.name,
                isRequired: field.isRequired,
                ...(field.type === 'select' && { options: field.options }),
                ...(field.defaultValue && { defaultValue: field.defaultValue }),
            });

            if (response.success) {
                toast.success('Custom field updated successfully');
                setEditingFieldId(null);
                loadCustomFields();
            }
        } catch (error) {
            console.error('Error updating custom field:', error);
            toast.error(error.message || 'Failed to update custom field');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteField = async (fieldId) => {
        if (!confirm('Are you sure you want to delete this custom field? All associated values will be lost.')) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.delete(`/custom-fields/${fieldId}`);

            if (response.success) {
                toast.success('Custom field deleted successfully');
                setCustomFields(customFields.filter(f => f.id !== fieldId));
            }
        } catch (error) {
            console.error('Error deleting custom field:', error);
            toast.error(error.message || 'Failed to delete custom field');
        } finally {
            setLoading(false);
        }
    };

    const handleAddOption = () => {
        if (newField.options.length >= 20) {
            toast.error('Maximum 20 options allowed');
            return;
        }
        setNewField({
            ...newField,
            options: [...newField.options, `Option ${newField.options.length + 1}`],
        });
    };

    const handleUpdateOption = (index, value) => {
        const updatedOptions = [...newField.options];
        updatedOptions[index] = value;
        setNewField({ ...newField, options: updatedOptions });
    };

    const handleDeleteOption = (index) => {
        setNewField({
            ...newField,
            options: newField.options.filter((_, i) => i !== index),
        });
    };

    const resetForm = () => {
        setNewField({
            name: '',
            key: '',
            type: 'text',
            isRequired: false,
            options: [],
            defaultValue: '',
        });
    };

    const handleFieldChange = (fieldId, updates) => {
        setCustomFields(customFields.map(f =>
            f.id === fieldId ? { ...f, ...updates } : f
        ));
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
        >
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Custom Fields</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Define dynamic fields that can be attached to users
                    </p>
                </div>
                <button
                    onClick={() => setIsAddingField(!isAddingField)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                    <SafeIcon icon={isAddingField ? FiX : FiPlus} className="w-4 h-4" />
                    <span>{isAddingField ? 'Cancel' : 'Add Field'}</span>
                </button>
            </div>

            {/* Add New Field Form */}
            {isAddingField && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/30"
                >
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4">Add New Custom Field</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Field Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={newField.name}
                                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                                placeholder="e.g., Phone Number"
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Field Key <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={newField.key}
                                onChange={(e) => setNewField({ ...newField, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                                placeholder="e.g., phone_number"
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white font-mono text-sm"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Lowercase letters, numbers, and underscores only
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Field Type
                            </label>
                            <select
                                value={newField.type}
                                onChange={(e) => setNewField({ ...newField, type: e.target.value, options: [] })}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                            >
                                {fieldTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Default Value
                            </label>
                            <input
                                type="text"
                                value={newField.defaultValue}
                                onChange={(e) => setNewField({ ...newField, defaultValue: e.target.value })}
                                placeholder="Optional"
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={newField.isRequired}
                                onChange={(e) => setNewField({ ...newField, isRequired: e.target.checked })}
                                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Required Field</span>
                        </label>
                    </div>

                    {/* Options for Select Type */}
                    {newField.type === 'select' && (
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Options <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2">
                                {newField.options.map((option, index) => (
                                    <div key={index} className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => handleUpdateOption(index, e.target.value)}
                                            className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                            placeholder={`Option ${index + 1}`}
                                        />
                                        <button
                                            onClick={() => handleDeleteOption(index)}
                                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={handleAddOption}
                                    className="flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                >
                                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                    <span>Add Option</span>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-2 mt-4">
                        <button
                            onClick={() => {
                                resetForm();
                                setIsAddingField(false);
                            }}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddField}
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <SafeIcon icon={FiSave} className="w-4 h-4" />
                            <span>{loading ? 'Creating...' : 'Create Field'}</span>
                        </button>
                    </div>
                </motion.div>
            )}

            {/* Existing Fields List */}
            {loading && customFields.length === 0 ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Loading custom fields...</p>
                </div>
            ) : customFields.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <p className="text-gray-600 dark:text-gray-400">No custom fields defined yet</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        Click "Add Field" to create your first custom field
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {customFields.map(field => (
                        <div
                            key={field.id}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                        <h4 className="font-medium text-gray-900 dark:text-white">{field.name}</h4>
                                        {field.isRequired && (
                                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">
                                                Required
                                            </span>
                                        )}
                                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded capitalize">
                                            {field.type}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                            {field.key}
                                        </span>
                                        {field.defaultValue && (
                                            <span>Default: {field.defaultValue}</span>
                                        )}
                                        {field.options && field.options.length > 0 && (
                                            <span>Options: {Array.isArray(field.options) ? field.options.join(', ') : field.options}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handleDeleteField(field.id)}
                                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        title="Delete field"
                                    >
                                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export default CustomFieldsTab;